import { useState, useEffect, useCallback } from "react"
import { Link, useNavigate } from "react-router"
import styles from "./growth-page.module.css"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import type { Course, Enrollment, Hackathon as SupabaseHackathon } from "@/lib/supabase/types"

// Types
type TabType = "courses" | "hackathon" | "jobs"

interface CourseCardData {
  id: string
  title: string
  progress: number
  chapter: string
  icon: "book" | "code" | "cpu"
  description?: string
}

interface HackathonCardData {
  id: string
  title: string
  date: string
  prize: string
  status: "upcoming" | "ongoing" | "ended"
  registrationStart: string
  eventStart: string
  eventEnd: string
}

// NOTE: AI岗位功能暂未接入招聘API，当前保留mock数据
// 后续将接入真实招聘数据源
interface Job {
  id: string
  title: string
  company: string
  salary: string
  tags: string[]
  location?: string
  remote?: boolean
}

// Mock jobs data (placeholder until recruitment API is integrated)
const jobs: Job[] = [
  { id: "1", title: "AI算法工程师", company: "字节跳动", salary: "35-50K", tags: ["Python", "深度学习", "NLP"], location: "北京", remote: true },
  { id: "2", title: "前端开发工程师", company: "美团", salary: "25-40K", tags: ["React", "TypeScript", "前端"], location: "上海" },
  { id: "3", title: "大模型研究员", company: "智谱AI", salary: "40-60K", tags: ["LLM", "PyTorch", "AI"], location: "北京", remote: true },
]

// Icons
const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
)

const CodeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
)

const CpuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
    <rect x="9" y="9" width="6" height="6" />
    <line x1="9" y1="1" x2="9" y2="4" />
    <line x1="15" y1="1" x2="15" y2="4" />
    <line x1="9" y1="20" x2="9" y2="23" />
    <line x1="15" y1="20" x2="15" y2="23" />
    <line x1="20" y1="9" x2="23" y2="9" />
    <line x1="20" y1="14" x2="23" y2="14" />
    <line x1="1" y1="9" x2="4" y2="9" />
    <line x1="1" y1="14" x2="4" y2="14" />
  </svg>
)

const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
)

const BriefcaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
)

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
)

const MapPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
)

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

// Tab Icon component
const TabIcon = ({ type, active }: { type: TabType; active: boolean }) => {
  const className = `${styles.tabIcon} ${active ? styles.tabIconActive : ""}`
  const icons = {
    courses: <BookIcon />,
    hackathon: <BoltIcon />,
    jobs: <BriefcaseIcon />,
  }
  return <span className={className}>{icons[type]}</span>
}

// Skeleton component
const Skeleton = ({ width = "100%", height = "20px", radius = "8px" }: { width?: string; height?: string; radius?: string }) => (
  <div
    className={styles.skeleton}
    style={{ width, height, borderRadius: radius }}
  />
)

// Empty State component
const EmptyState = ({ title, message }: { title: string; message: string }) => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>
      <EmptyIcon />
    </div>
    <h3 className={styles.emptyTitle}>{title}</h3>
    <p className={styles.emptyMessage}>{message}</p>
    <Link to="/" className={styles.emptyAction}>
      去探索
    </Link>
  </div>
)

// Helpers

function getCourseIcon(category: string | null): "book" | "code" | "cpu" {
  if (!category) return "book"
  const lower = category.toLowerCase()
  if (lower.includes("agent") || lower.includes("开发") || lower.includes("编程")) return "code"
  if (lower.includes("机器学习") || lower.includes("ml") || lower.includes("深度学习") || lower.includes("ai")) return "cpu"
  return "book"
}

function formatPrize(bbp: number): string {
  return `¥${bbp.toLocaleString()}`
}

function formatDate(iso: string): string {
  return iso.slice(0, 10)
}

function mapHackathonStatus(status: string): "upcoming" | "ongoing" | "ended" {
  switch (status) {
    case "ongoing":
    case "registration":
      return "ongoing"
    case "judging":
    case "completed":
      return "ended"
    default:
      return "upcoming"
  }
}

function mapCourseToCardData(course: Course, enrollment?: Enrollment): CourseCardData {
  const progress = enrollment?.progress ?? 0
  const chapterLabel = progress > 0
    ? `第${Math.round(progress / 100 * course.total_chapters)}章 / 共${course.total_chapters}章`
    : `${course.total_chapters}章节 · ${course.enrolled_count}人学习`

  return {
    id: course.id,
    title: course.title,
    progress,
    chapter: chapterLabel,
    icon: getCourseIcon(course.category),
    description: course.description ?? undefined,
  }
}

function mapHackathonToCardData(h: SupabaseHackathon): HackathonCardData {
  return {
    id: h.id,
    title: h.title,
    date: formatDate(h.event_start),
    prize: formatPrize(h.prize_pool_bbp),
    status: mapHackathonStatus(h.status),
    registrationStart: h.registration_start,
    eventStart: h.event_start,
    eventEnd: h.event_end,
  }
}

// Course Card component
const CourseCard = ({ course, isLarge = false }: { course: CourseCardData; isLarge?: boolean }) => {
  const navigate = useNavigate()
  const iconMap = { book: <BookIcon />, code: <CodeIcon />, cpu: <CpuIcon /> }

  if (isLarge) {
    return (
      <div
        className={styles.courseCard}
        onClick={() => navigate(`/growth/courses/${course.id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && navigate(`/growth/courses/${course.id}`)}
      >
        <div className={styles.courseCardHeader}>
          <div className={styles.courseIcon}>{iconMap[course.icon]}</div>
          <div className={styles.courseInfo}>
            <h3>{course.title}</h3>
            <p>{course.chapter}</p>
          </div>
        </div>
        <div className={styles.progressContainer}>
          <div className={styles.progress}>
            <div className={styles.progressBar} style={{ width: `${course.progress}%` }} />
          </div>
          <span className={styles.progressText}>{course.progress}%</span>
        </div>
      </div>
    )
  }

  return (
    <div
      className={styles.smallCard}
      onClick={() => navigate(`/growth/courses/${course.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/growth/courses/${course.id}`)}
    >
      <div className={styles.smallCardIcon}>{iconMap[course.icon]}</div>
      <div className={styles.smallCardInfo}>
        <h4>{course.title}</h4>
        <span>{course.chapter}</span>
      </div>
      <ArrowRightIcon />
    </div>
  )
}

// Hackathon Card component
const HackathonCard = ({ hackathon }: { hackathon: HackathonCardData }) => {
  const navigate = useNavigate()
  const statusClass = {
    upcoming: styles.statusUpcoming,
    ongoing: styles.statusOngoing,
    ended: styles.statusEnded,
  }[hackathon.status]

  return (
    <div
      className={styles.hackathonCard}
      onClick={() => navigate(`/hackathon/${hackathon.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/hackathon/${hackathon.id}`)}
    >
      <div className={styles.hackathonCardHeader}>
        <div className={styles.hackathonCardIcon}>
          <BoltIcon />
        </div>
        <div className={styles.hackathonCardInfo}>
          <h4>{hackathon.title}</h4>
          <span>{hackathon.date}</span>
        </div>
        <span className={`${styles.statusBadge} ${statusClass}`}>
          {hackathon.status === "upcoming" ? "即将开始" : hackathon.status === "ongoing" ? "进行中" : "已结束"}
        </span>
      </div>
      <div className={styles.hackathonPrize}>
        <span className={styles.prizeLabel}>奖金池</span>
        <span className={styles.prizeValue}>{hackathon.prize}</span>
      </div>
    </div>
  )
}

// Job Card component
const JobCard = ({ job }: { job: Job }) => {
  const [applied, setApplied] = useState(false)
  const [applying, setApplying] = useState(false)

  const handleApply = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (applied || applying) return

    setApplying(true)
    // Simulate API call
    setTimeout(() => {
      setApplying(false)
      setApplied(true)
    }, 1000)
  }

  return (
    <div className={styles.jobCard}>
      <div className={styles.jobHeader}>
        <div>
          <h3 className={styles.jobTitle}>{job.title}</h3>
          <div className={styles.jobCompany}>
            <div className={styles.jobCompanyLogo}>{job.company.slice(0, 1)}</div>
            <span className={styles.jobCompanyName}>{job.company}</span>
            {job.remote && <span className={styles.remoteTag}>远程</span>}
          </div>
          {job.location && (
            <div className={styles.jobLocation}>
              <MapPinIcon />
              <span>{job.location}</span>
            </div>
          )}
        </div>
        <p className={styles.jobSalary}>{job.salary}</p>
      </div>
      <div className={styles.jobTags}>
        {job.tags.map((tag) => (
          <span key={tag} className={styles.jobTag}>{tag}</span>
        ))}
      </div>
      <button
        className={`${styles.applyBtn} ${applied ? styles.appliedBtn : ""}`}
        onClick={handleApply}
        disabled={applied || applying}
      >
        {applying ? "申请中..." : applied ? "已申请" : "立即申请"}
      </button>
    </div>
  )
}

// Data fetching hook
interface GrowthData {
  courses: CourseCardData[]
  hackathons: HackathonCardData[]
  enrollments: Map<string, Enrollment>
  loading: boolean
  coursesError: boolean
  hackathonsError: boolean
}

function useGrowthData(): GrowthData {
  const [courses, setCourses] = useState<CourseCardData[]>([])
  const [hackathons, setHackathons] = useState<HackathonCardData[]>([])
  const [enrollments, setEnrollments] = useState<Map<string, Enrollment>>(new Map())
  const [loading, setLoading] = useState(true)
  const [coursesError, setCoursesError] = useState(false)
  const [hackathonsError, setHackathonsError] = useState(false)

  const user = useAuthStore((s) => s.user)

  const fetchData = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) {
      setLoading(false)
      setCoursesError(true)
      setHackathonsError(true)
      return
    }

    setLoading(true)

    // Fetch courses, enrollments, and hackathons in parallel
    const [coursesResult, hackathonsResult, enrollmentsResult] = await Promise.all([
      supabase
        .from("courses")
        .select("*")
        .eq("status", "published")
        .order("enrolled_count", { ascending: false })
        .limit(20),
      supabase
        .from("hackathons")
        .select("*")
        .order("event_start", { ascending: false })
        .limit(20),
      user
        ? supabase
            .from("enrollments")
            .select("*")
            .eq("user_id", user.id)
            .in("status", ["active", "completed"])
        : Promise.resolve({ data: null, error: { message: "not authenticated" } }),
    ])

    // Process courses
    if (coursesResult.error) {
      setCoursesError(true)
      setCourses([])
    } else if (coursesResult.data) {
      const enrollmentMap = new Map<string, Enrollment>()
      if (enrollmentsResult.data && !enrollmentsResult.error) {
        for (const e of enrollmentsResult.data as Enrollment[]) {
          enrollmentMap.set(e.course_id, e)
        }
      }
      setEnrollments(enrollmentMap)

      const cardData = (coursesResult.data as Course[]).map((c) =>
        mapCourseToCardData(c, enrollmentMap.get(c.id))
      )
      setCourses(cardData)
      setCoursesError(false)
    }

    // Process hackathons
    if (hackathonsResult.error) {
      setHackathonsError(true)
      setHackathons([])
    } else if (hackathonsResult.data) {
      const cardData = (hackathonsResult.data as SupabaseHackathon[]).map(mapHackathonToCardData)
      setHackathons(cardData)
      setHackathonsError(false)
    }

    setLoading(false)
  }, [user])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { courses, hackathons, enrollments, loading, coursesError, hackathonsError }
}

// Main component
export default function GrowthPage() {
  const [activeTab, setActiveTab] = useState<TabType>("courses")
  const [animating, setAnimating] = useState(false)
  const { courses, hackathons, loading, coursesError, hackathonsError } = useGrowthData()

  // Tab switch with animation
  const handleTabChange = (tab: TabType) => {
    if (tab === activeTab) return
    setAnimating(true)
    setTimeout(() => {
      setActiveTab(tab)
      setAnimating(false)
    }, 200)
  }

  const tabs: { key: TabType; label: string }[] = [
    { key: "courses", label: "精品课程" },
    { key: "hackathon", label: "黑客松" },
    { key: "jobs", label: "AI岗位" },
  ]

  // Courses split into in-progress and remaining
  const inProgressCourses = courses.filter((c) => c.progress > 0)
  const remainingCourses = courses.filter((c) => c.progress === 0)

  if (loading) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <Skeleton width="120px" height="28px" />
        </header>
        <div className={styles.banner}>
          <Skeleton width="200px" height="20px" />
        </div>
        <div className={styles.tabs}>
          {tabs.map(() => <Skeleton height="42px" />)}
        </div>
        <div className={styles.skeletonSection}>
          <Skeleton height="24px" width="100px" />
          <div className={styles.skeletonCards}>
            <Skeleton height="120px" />
            <Skeleton height="80px" />
            <Skeleton height="80px" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>成长中心</h1>
      </header>

      {/* Banner */}
      <Link to="/discovery" className={styles.banner}>
        <div className={styles.bannerContent}>
          <h2 className={styles.bannerTitle}>开启你的AI成长之路</h2>
          <p className={styles.bannerSubtitle}>精品课程 · 实战黑客松 · 名企岗位</p>
        </div>
      </Link>

      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.activeTab : ""}`}
            onClick={() => handleTabChange(tab.key)}
          >
            <TabIcon type={tab.key} active={activeTab === tab.key} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className={`${styles.section} ${animating ? styles.sectionHidden : styles.sectionVisible}`}>
        {/* Courses Tab */}
        {activeTab === "courses" && (
          <>
            {coursesError ? (
              <EmptyState title="课程数据暂不可用" message="课程功能即将上线，敬请期待" />
            ) : (
              <>
                <h2 className={styles.sectionTitle}>
                  <ClockIcon />
                  进行中
                </h2>
                {inProgressCourses.length > 0 ? (
                  <CourseCard course={inProgressCourses[0]} isLarge />
                ) : (
                  <EmptyState title="暂无进行中课程" message="开始学习新课程吧" />
                )}

                <h2 className={styles.sectionTitle}>
                  <BookIcon />
                  全部课程
                </h2>
                <div className={styles.courseList}>
                  {remainingCourses.map((course) => (
                    <CourseCard key={course.id} course={course} />
                  ))}
                </div>

                <Link to="/courses" className={styles.loadMoreBtn}>
                  <span>查看更多课程</span>
                  <ArrowRightIcon />
                </Link>
              </>
            )}
          </>
        )}

        {/* Hackathon Tab */}
        {activeTab === "hackathon" && (
          <>
            {hackathonsError ? (
              <EmptyState title="黑客松数据暂不可用" message="黑客松功能即将上线，敬请期待" />
            ) : hackathons.length > 0 ? (
              <>
                {hackathons.map((hackathon) => (
                  <HackathonCard key={hackathon.id} hackathon={hackathon} />
                ))}

                {/* Timeline - derived from first hackathon */}
                {hackathons[0] && (
                  <>
                    <h2 className={styles.sectionTitle}>赛程安排</h2>
                    <div className={styles.timeline}>
                      <div className={styles.timelineItem}>
                        <div className={`${styles.timelineDot} ${styles.timelineDotActive}`} />
                        <p className={styles.timelineDate}>{formatDate(hackathons[0].registrationStart)}</p>
                        <p className={styles.timelineContent}>报名开启</p>
                      </div>
                      <div className={styles.timelineItem}>
                        <div className={styles.timelineDot} />
                        <p className={styles.timelineDate}>{formatDate(hackathons[0].eventStart)}</p>
                        <p className={styles.timelineContent}>作品提交</p>
                      </div>
                      <div className={styles.timelineItem}>
                        <div className={styles.timelineDot} />
                        <p className={styles.timelineDate}>{formatDate(hackathons[0].eventEnd)}</p>
                        <p className={styles.timelineContent}>决赛答辩</p>
                      </div>
                    </div>
                  </>
                )}

                <Link to="/hackathons" className={styles.loadMoreBtn}>
                  <span>查看更多黑客松</span>
                  <ArrowRightIcon />
                </Link>
              </>
            ) : (
              <EmptyState title="暂无黑客松" message="精彩赛事即将上线" />
            )}
          </>
        )}

        {/* Jobs Tab - mock data, pending recruitment API integration */}
        {activeTab === "jobs" && (
          <>
            {jobs.length > 0 ? (
              <>
                {jobs.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
                <Link to="/jobs" className={styles.loadMoreBtn}>
                  <span>查看更多岗位</span>
                  <ArrowRightIcon />
                </Link>
              </>
            ) : (
              <EmptyState title="暂无岗位" message="敬请期待更多AI岗位" />
            )}
          </>
        )}
      </div>
    </div>
  )
}
